import {Type} from 'io-ts';
import {isUndefined} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useDebounce} from '../../../../../../srcCommon/helpers/hookHelpers';
import {SearchToken} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {ApiType} from '../../../../../models/api/apiTypes';
import {CompanyModel} from '../../../../../models/api/companyModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSearchSuggestionItemProps,
  InboxesSearchSuggestionsBaseProps,
  InboxesSearchSuggestionsResourceProps,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
  renderResourceItem,
  SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownResourceAsyncProps<T>
  extends InboxesSearchSuggestionsBaseProps,
    InboxesSearchSuggestionsResourceProps<T> {
  urlBuilder: (company: CompanyModel, query: string) => string;
  apiType: ApiType<Type<ReadonlyArray<T>, ReadonlyArray<T>, unknown>>;
  transform?: (items: ReadonlyArray<T>) => ReadonlyArray<T>;
}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownResourceAsync = <T,>(
  props: InboxesSearchSuggestionsDropdownResourceAsyncProps<T>,
) => {
  const {
    activeToken,
    urlBuilder,
    apiType,
    transform,
    modifiers,
    valueKey,
    primaryKey,
    secondaryKey,
    sectionTitle,
  } = props;

  const company = useAppSelector(getCompany);

  const [items, setItems] = useState<ReadonlyArray<T & InboxesSearchSuggestionItemProps>>([]);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = () => {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    fetchItemsAsync(activeToken);

    return () => maybeCancelRequest();
  });

  const fetchItemsAsync = async (searchToken: SearchToken) => {
    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    if (
      isUndefined(searchToken.value) ||
      isUndefined(searchToken.modifier) ||
      !modifiers.includes(searchToken.modifier)
    ) {
      return setItems([]);
    }

    const {token} = cancelTokenSource;
    const encodedQuery = encodeURIComponent(searchToken.value);
    const path = urlBuilder(company, encodedQuery);

    try {
      const matches = await fetchApiAsync(apiType, path, {cancelToken: token});
      const transformedMatches = transform ? transform(matches) : matches;

      return setItems(
        transformedMatches.map((match) => ({
          ...match,
          value: `${searchToken.modifier}:${match[valueKey]}`,
          primaryLabel: <FuzzyHighlight query={searchToken.value} text={String(match[primaryKey])} />,
          secondaryLabel: (
            <FuzzyHighlight query={searchToken.value} text={String(match[secondaryKey || valueKey])} />
          ),
        })),
      );
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return null;
    }
  };

  const fetchItemsAsyncDebounced = useDebounce(fetchItemsAsync, SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS);

  useEffect(() => {
    fetchItemsAsyncDebounced(activeToken);
  }, [activeToken, fetchItemsAsyncDebounced]);

  if (items.length === 0) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>{sectionTitle}</InboxesSuggestionsDropdownSectionTitle>
        {items.map((item) => renderResourceItem(item, props, activeToken.modifier))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};
